<template>
    <div class="student-details-wrapper">
        <!-- Loading State -->
        <div v-if="isLoading" class="flex justify-content-center align-items-center py-8">
            <i class="pi pi-spinner pi-spin" style="font-size: 2rem; color: var(--accent-500)"></i>
        </div>

        <!-- Error State -->
        <div v-else-if="loadError" class="flex align-items-center justify-content-center py-8">
            <div class="text-center">
                <i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: var(--orange-500)"></i>
                <p class="mt-2 text-color-secondary">Failed to load student details.</p>
                <Button label="Retry" class="accent-button mt-3" @click="fetchDetails" />
            </div>
        </div>

        <!-- Content -->
        <div v-else-if="details" class="surface-section">
            <!-- Profile Header Card -->
            <div class="card profile-header-card">
                <div class="flex flex-column md:flex-row align-items-center gap-4">
                    <img :src="profilePhoto" class="profile-image" alt="Profile" />
                    <div class="flex flex-column md:flex-row justify-content-between w-full align-items-start md:align-items-center gap-3">
                        <div class="flex flex-column">
                            <h1 class="H5-SemiBold mb-1">{{ profile?.username || 'N/A' }}</h1>
                            <p class="caption mb-2">{{ profile?.email || 'N/A' }}</p>
                            <div class="flex flex-wrap gap-2">
                                <Tag :value="profile?.isActive ? 'Active' : 'Inactive'"
                                    :severity="profile?.isActive ? 'success' : 'danger'" rounded />
                                <Tag :value="profile?.emailVerified ? 'Email Verified' : 'Email Unverified'"
                                    :severity="profile?.emailVerified ? 'success' : 'secondary'" rounded />
                                <Tag :value="profile?.twoFactorEnabled ? '2FA Enabled' : '2FA Disabled'"
                                    :severity="profile?.twoFactorEnabled ? 'success' : 'secondary'" rounded />
                                <Tag :value="profile?.registrationCompleted ? 'Registration Complete' : 'Registration Pending'"
                                    :severity="profile?.registrationCompleted ? 'success' : 'warn'" rounded />
                            </div>
                        </div>
                        <div class="flex flex-wrap gap-3">
                            <div class="stat-box">
                                <span class="stat-value">{{ classes.length }}</span>
                                <span class="stat-label">Classes</span>
                            </div>
                            <div class="stat-box">
                                <span class="stat-value">{{ instances.length }}</span>
                                <span class="stat-label">Sessions</span>
                            </div>
                            <div class="stat-box">
                                <span class="stat-value">{{ formatCurrency(totalPaid) }}</span>
                                <span class="stat-label">Total Paid</span>
                            </div>
                            <div class="stat-box">
                                <span class="stat-value">{{ reviews.length }}</span>
                                <span class="stat-label">Reviews</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tabs -->
            <div class="card mt-4">
                <Tabs value="profile">
                    <TabList>
                        <Tab value="profile" class="Label">Profile</Tab>
                        <Tab value="academic" class="Label">Academic</Tab>
                        <Tab value="classes" class="Label">Classes</Tab>
                        <Tab value="billing" class="Label">Billing & Payments</Tab>
                        <Tab value="activity" class="Label">Activity</Tab>
                    </TabList>
                    <TabPanels>
                        <!-- ===================== PROFILE TAB ===================== -->
                        <TabPanel value="profile">
                            <div class="grid">
                                <div class="col-12 md:col-6">
                                    <h3 class="Paragraph-Medium font-semibold mb-3">Personal Information</h3>
                                    <ul class="list-none p-0 m-0">
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-user text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Username:</span>
                                                <span class="Label">{{ profile?.username || 'N/A' }}</span>
                                            </div>
                                        </li>
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-envelope text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Email:</span>
                                                <span class="Label">{{ profile?.email || 'N/A' }}</span>
                                            </div>
                                        </li>
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-id-card text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Role:</span>
                                                <span class="Label">{{ cleanupLables(profile?.role) || 'N/A' }}</span>
                                            </div>
                                        </li>
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-calendar text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Date of Birth:</span>
                                                <span class="Label">{{ formatToDisplayDate(profile?.dateOfBirth) }}</span>
                                            </div>
                                        </li>
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-flag text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Nationality:</span>
                                                <span class="Label">{{ profile?.nationality || 'Sri Lanka' }}</span>
                                            </div>
                                        </li>
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-clock text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Joined:</span>
                                                <span class="Label">{{ formatToDisplayDate(profile?.created_at) }}</span>
                                            </div>
                                        </li>
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-refresh text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Last Updated:</span>
                                                <span class="Label">{{ formatToDisplayDate(profile?.updated_at) }}</span>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                                <div class="col-12 md:col-6">
                                    <h3 class="Paragraph-Medium font-semibold mb-3">Education & Preferences</h3>
                                    <ul class="list-none p-0 m-0">
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-star text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Grade / Education Level:</span>
                                                <span class="Label">{{ cleanupLables(profile?.currentEducationLevel) || 'N/A' }}</span>
                                            </div>
                                        </li>
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-building text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Institution:</span>
                                                <span class="Label">{{ profile?.currentInstitution || 'N/A' }}</span>
                                            </div>
                                        </li>
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-globe text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Exam Medium:</span>
                                                <span class="Label">{{ cleanupLables(profile?.selectedExamMedium) || 'N/A' }}</span>
                                            </div>
                                        </li>
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-comments text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Preferred Medium:</span>
                                                <span class="Label">{{ cleanupLables(profile?.preferredMedium) || 'N/A' }}</span>
                                            </div>
                                        </li>
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-wallet text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Budget:</span>
                                                <span class="Label">
                                                    {{ profile?.minimumBudget != null ? formatCurrency(profile.minimumBudget) : 'N/A' }}
                                                    <span v-if="profile?.maximumBudget != null"> - {{ formatCurrency(profile.maximumBudget) }}</span>
                                                </span>
                                            </div>
                                        </li>
                                        <li class="flex align-items-start gap-2 mb-3">
                                            <i class="pi pi-users text-500 mt-1"></i>
                                            <div class="flex flex-column">
                                                <span class="Paragraph-Medium mb-1">Created By:</span>
                                                <span class="Label">{{ cleanupLables(profile?.createdBy) || 'N/A' }}</span>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <Divider />

                            <div class="grid">
                                <div class="col-12 md:col-6">
                                    <h3 class="Paragraph-Medium font-semibold mb-3">Learning Preferences</h3>
                                    <div class="mb-3">
                                        <span class="Paragraph-Medium block mb-2">Motivation:</span>
                                        <div class="flex flex-wrap gap-2">
                                            <Tag v-for="(m, i) in profile?.learningMotivation" :key="i"
                                                :value="cleanupLables(m)" severity="info" rounded />
                                            <span v-if="!profile?.learningMotivation?.length" class="Label">N/A</span>
                                        </div>
                                    </div>
                                    <div class="mb-3">
                                        <span class="Paragraph-Medium block mb-2">Interested Subjects:</span>
                                        <div class="flex flex-wrap gap-2">
                                            <Tag v-for="(s, i) in profile?.interestedSubjects" :key="i"
                                                :value="cleanupLables(s)" severity="secondary" rounded />
                                            <span v-if="!profile?.interestedSubjects?.length" class="Label">N/A</span>
                                        </div>
                                    </div>
                                    <div class="mb-3">
                                        <span class="Paragraph-Medium block mb-2">Preferred Learning Mode:</span>
                                        <div class="flex flex-wrap gap-2">
                                            <Tag v-for="(m, i) in profile?.preferredLearningMode" :key="i"
                                                :value="cleanupLables(m)" rounded />
                                            <span v-if="!profile?.preferredLearningMode?.length" class="Label">N/A</span>
                                        </div>
                                    </div>
                                    <div>
                                        <span class="Paragraph-Medium block mb-2">Preferred Class Time:</span>
                                        <div class="flex flex-wrap gap-2">
                                            <Tag v-for="(t, i) in profile?.preferredClassTime" :key="i"
                                                :value="cleanupLables(t)" severity="warn" rounded />
                                            <span v-if="!profile?.preferredClassTime?.length" class="Label">N/A</span>
                                        </div>
                                    </div>
                                    <div class="mt-3 flex gap-4">
                                        <span class="Label">
                                            Individual Class:
                                            <Tag :value="profile?.preferredIndividualClass ? 'Yes' : 'No'"
                                                :severity="profile?.preferredIndividualClass ? 'success' : 'secondary'" rounded />
                                        </span>
                                        <span class="Label">
                                            Group Class:
                                            <Tag :value="profile?.preferredGroupClass ? 'Yes' : 'No'"
                                                :severity="profile?.preferredGroupClass ? 'success' : 'secondary'" rounded />
                                        </span>
                                    </div>
                                </div>
                                <div class="col-12 md:col-6">
                                    <h3 class="Paragraph-Medium font-semibold mb-3">Guardians</h3>
                                    <div v-if="profile?.guardians?.length" class="flex flex-column gap-3">
                                        <div v-for="(g, i) in profile.guardians" :key="i" class="guardian-box">
                                            <div class="flex justify-content-between align-items-center mb-2">
                                                <span class="Paragraph-Medium font-semibold">{{ g.name || 'N/A' }}</span>
                                                <Tag v-if="g.isPrimary" value="Primary" severity="success" rounded />
                                            </div>
                                            <div class="flex flex-column gap-1">
                                                <span class="Label"><i class="pi pi-phone mr-2"></i>{{ g.contactNumber || 'N/A' }}</span>
                                                <span class="Label"><i class="pi pi-envelope mr-2"></i>{{ g.email || 'N/A' }}</span>
                                                <span class="Label"><i class="pi pi-bell mr-2"></i>Alert Sent:
                                                    <Tag :value="g.isAlertSent ? 'Yes' : 'No'"
                                                        :severity="g.isAlertSent ? 'success' : 'secondary'" rounded />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <p v-else class="Label">No guardians listed.</p>
                                </div>
                            </div>
                        </TabPanel>

                        <!-- ===================== ACADEMIC TAB ===================== -->
                        <TabPanel value="academic">
                            <div class="mb-5">
                                <h3 class="Paragraph-Medium font-semibold mb-3">Notes ({{ notes.length }})</h3>
                                <DataTable v-if="notes.length" :value="notes" class="p-datatable-sm" dataKey="_id"
                                    paginator :rows="10" :rowsPerPageOptions="[5, 10, 20]">
                                    <Column field="title" header="Title">
                                        <template #body="{ data }">{{ data.title || 'Untitled' }}</template>
                                    </Column>
                                    <Column header="Subject">
                                        <template #body="{ data }">{{ cleanupLables(data.subject) || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Grade">
                                        <template #body="{ data }">{{ cleanupLables(data.grade) || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Created">
                                        <template #body="{ data }">{{ formatToDisplayDate(data.created_at) }}</template>
                                    </Column>
                                    <template #empty>
                                        <div class="text-center py-4">
                                            <i class="pi pi-inbox" style="font-size: 1.5rem; color: var(--surface-400)"></i>
                                            <p class="mt-2 text-color-secondary">No notes found</p>
                                        </div>
                                    </template>
                                </DataTable>
                                <p v-else class="Label">No notes available for this student.</p>
                            </div>

                            <Divider />

                            <div>
                                <h3 class="Paragraph-Medium font-semibold mb-3">Exam Results ({{ examResults.length }})</h3>
                                <DataTable v-if="examResults.length" :value="examResults" class="p-datatable-sm" dataKey="_id"
                                    paginator :rows="10" :rowsPerPageOptions="[5, 10, 20]">
                                    <Column header="Exam">
                                        <template #body="{ data }">{{ cleanupLables(data.examName) || cleanupLables(data.exam) || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Grade">
                                        <template #body="{ data }">{{ cleanupLables(data.grade) || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Subject">
                                        <template #body="{ data }">{{ cleanupLables(data.subject) || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Results">
                                        <template #body="{ data }">
                                            <span v-if="data.results != null">{{ data.results }}</span>
                                            <span v-else-if="data.marks != null">{{ data.marks }}</span>
                                            <span v-else>N/A</span>
                                        </template>
                                    </Column>
                                    <Column header="Year">
                                        <template #body="{ data }">{{ data.year || 'N/A' }}</template>
                                    </Column>
                                    <template #empty>
                                        <div class="text-center py-4">
                                            <i class="pi pi-inbox" style="font-size: 1.5rem; color: var(--surface-400)"></i>
                                            <p class="mt-2 text-color-secondary">No exam results found</p>
                                        </div>
                                    </template>
                                </DataTable>
                                <p v-else class="Label">No exam results available for this student.</p>
                            </div>
                        </TabPanel>

                        <!-- ===================== CLASSES TAB ===================== -->
                        <TabPanel value="classes">
                            <div class="mb-5">
                                <h3 class="Paragraph-Medium font-semibold mb-3">Classes ({{ classes.length }})</h3>
                                <DataTable v-if="classes.length" :value="classes" class="p-datatable-sm" dataKey="_id"
                                    paginator :rows="10" :rowsPerPageOptions="[5, 10, 20]">
                                    <Column header="Subject">
                                        <template #body="{ data }">{{ cleanupLables(data.subject) || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Grade">
                                        <template #body="{ data }">{{ cleanupLables(data.grade) || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Tutor">
                                        <template #body="{ data }">
                                            <div class="flex flex-column">
                                                <span>{{ data.tutorId?.firstName }} {{ data.tutorId?.lastName }}</span>
                                                <small class="text-gray-500">{{ data.tutorId?.email }}</small>
                                            </div>
                                        </template>
                                    </Column>
                                    <Column header="Model">
                                        <template #body="{ data }">{{ cleanupLables(data.classModel) || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Rate">
                                        <template #body="{ data }">{{ formatCurrency(data.hourlyRate, 'LKR') }}</template>
                                    </Column>
                                    <Column header="Session Fee">
                                        <template #body="{ data }">{{ formatCurrency(data.sessionFee, 'LKR') }}</template>
                                    </Column>
                                    <Column header="Status">
                                        <template #body="{ data }">
                                            <Tag :value="cleanupLables(data.status)" :severity="statusSeverity(data.status)" rounded />
                                        </template>
                                    </Column>
                                    <Column header="Recurrence">
                                        <template #body="{ data }">
                                            <span v-if="data.recurrence?.enabled">
                                                Every {{ data.recurrence.repeatWeeks }} week(s) on {{ data.recurrence.repeatOn?.map(d => cleanupLables(d)).join(', ') }}
                                            </span>
                                            <span v-else>N/A</span>
                                        </template>
                                    </Column>
                                    <template #empty>
                                        <div class="text-center py-4">
                                            <i class="pi pi-inbox" style="font-size: 1.5rem; color: var(--surface-400)"></i>
                                            <p class="mt-2 text-color-secondary">No classes found</p>
                                        </div>
                                    </template>
                                </DataTable>
                                <p v-else class="Label">No classes available for this student.</p>
                            </div>

                            <Divider />

                            <div class="mb-5">
                                <h3 class="Paragraph-Medium font-semibold mb-3">Class Instances ({{ instances.length }})</h3>
                                <DataTable v-if="instances.length" :value="instances" class="p-datatable-sm" dataKey="_id"
                                    paginator :rows="10" :rowsPerPageOptions="[5, 10, 20]">
                                    <Column header="Date">
                                        <template #body="{ data }">{{ formatToDisplayDate(data.date) }}</template>
                                    </Column>
                                    <Column header="Start">
                                        <template #body="{ data }">{{ formatToDisplayTime(data.start) }}</template>
                                    </Column>
                                    <Column header="End">
                                        <template #body="{ data }">{{ formatToDisplayTime(data.end) }}</template>
                                    </Column>
                                    <Column header="Status">
                                        <template #body="{ data }">
                                            <Tag :value="cleanupLables(data.status)" :severity="statusSeverity(data.status)" rounded />
                                        </template>
                                    </Column>
                                    <Column header="Payment">
                                        <template #body="{ data }">
                                            <Tag :value="data.paymentStatus || (data.isPaid ? 'Paid' : 'Unpaid')"
                                                :severity="data.isPaid ? 'success' : 'warn'" rounded />
                                        </template>
                                    </Column>
                                    <Column header="Meet Link">
                                        <template #body="{ data }">
                                            <a v-if="data.meetLink" :href="data.meetLink" target="_blank" rel="noopener" class="Label-teal">
                                                <i class="pi pi-video mr-1"></i>Join
                                            </a>
                                            <span v-else>N/A</span>
                                        </template>
                                    </Column>
                                    <template #empty>
                                        <div class="text-center py-4">
                                            <i class="pi pi-inbox" style="font-size: 1.5rem; color: var(--surface-400)"></i>
                                            <p class="mt-2 text-color-secondary">No class instances found</p>
                                        </div>
                                    </template>
                                </DataTable>
                                <p v-else class="Label">No class instances available for this student.</p>
                            </div>

                            <Divider />

                            <div>
                                <h3 class="Paragraph-Medium font-semibold mb-3">Reschedules ({{ reschedules.length }})</h3>
                                <DataTable v-if="reschedules.length" :value="reschedules" class="p-datatable-sm" dataKey="_id"
                                    paginator :rows="10" :rowsPerPageOptions="[5, 10, 20]">
                                    <Column header="Requested By">
                                        <template #body="{ data }">{{ cleanupLables(data.requestedBy) || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Old Date">
                                        <template #body="{ data }">{{ formatToDisplayDate(data.oldDate) }}</template>
                                    </Column>
                                    <Column header="New Date">
                                        <template #body="{ data }">{{ formatToDisplayDate(data.newDate) }}</template>
                                    </Column>
                                    <Column header="Status">
                                        <template #body="{ data }">
                                            <Tag :value="cleanupLables(data.status)" :severity="statusSeverity(data.status)" rounded />
                                        </template>
                                    </Column>
                                    <template #empty>
                                        <div class="text-center py-4">
                                            <i class="pi pi-inbox" style="font-size: 1.5rem; color: var(--surface-400)"></i>
                                            <p class="mt-2 text-color-secondary">No reschedule requests found</p>
                                        </div>
                                    </template>
                                </DataTable>
                                <p v-else class="Label">No reschedule requests for this student.</p>
                            </div>
                        </TabPanel>

                        <!-- ===================== BILLING & PAYMENTS TAB ===================== -->
                        <TabPanel value="billing">
                            <div class="mb-5">
                                <h3 class="Paragraph-Medium font-semibold mb-3">Subscriptions ({{ subscriptions.length }})</h3>
                                <DataTable v-if="subscriptions.length" :value="subscriptions" class="p-datatable-sm" dataKey="_id"
                                    paginator :rows="10" :rowsPerPageOptions="[5, 10, 20]">
                                    <Column header="Package">
                                        <template #body="{ data }">{{ data.packageId?.name || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Status">
                                        <template #body="{ data }">
                                            <Tag :value="cleanupLables(data.status)" :severity="statusSeverity(data.status)" rounded />
                                        </template>
                                    </Column>
                                    <Column header="Amount">
                                        <template #body="{ data }">{{ formatCurrency(data.amount, data.currency) }}</template>
                                    </Column>
                                    <Column header="Billing Cycle">
                                        <template #body="{ data }">{{ cleanupLables(data.billingCycle) || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Start Date">
                                        <template #body="{ data }">{{ formatToDisplayDate(data.startDate) }}</template>
                                    </Column>
                                    <Column header="End Date">
                                        <template #body="{ data }">{{ formatToDisplayDate(data.endDate) }}</template>
                                    </Column>
                                    <Column header="Auto Renew">
                                        <template #body="{ data }">
                                            <Tag :value="data.autoRenew ? 'Yes' : 'No'" :severity="data.autoRenew ? 'success' : 'secondary'" rounded />
                                        </template>
                                    </Column>
                                    <Column header="Sponsored By">
                                        <template #body="{ data }">
                                            <span v-if="data.sponsoredBy">{{ data.sponsoredBy.username || 'N/A' }}</span>
                                            <span v-else>Self</span>
                                        </template>
                                    </Column>
                                    <template #empty>
                                        <div class="text-center py-4">
                                            <i class="pi pi-inbox" style="font-size: 1.5rem; color: var(--surface-400)"></i>
                                            <p class="mt-2 text-color-secondary">No subscriptions found</p>
                                        </div>
                                    </template>
                                </DataTable>
                                <p v-else class="Label">No subscriptions for this student.</p>
                            </div>

                            <Divider />

                            <div class="mb-5">
                                <h3 class="Paragraph-Medium font-semibold mb-3">Payments ({{ payments.length }})</h3>
                                <DataTable v-if="payments.length" :value="payments" class="p-datatable-sm" dataKey="_id"
                                    paginator :rows="10" :rowsPerPageOptions="[5, 10, 20]">
                                    <Column header="Date">
                                        <template #body="{ data }">{{ formatToDisplayDate(data.created_at) }}</template>
                                    </Column>
                                    <Column header="Type">
                                        <template #body="{ data }">{{ cleanupLables(data.paymentType) || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Amount">
                                        <template #body="{ data }">{{ formatCurrency(data.amount, data.currency) }}</template>
                                    </Column>
                                    <Column header="Method">
                                        <template #body="{ data }">{{ data.paymentMethod || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Status">
                                        <template #body="{ data }">
                                            <Tag :value="cleanupLables(data.status)" :severity="paymentStatusSeverity(data.status)" rounded />
                                        </template>
                                    </Column>
                                    <Column header="Order ID">
                                        <template #body="{ data }">
                                            <span class="Label text-xs">{{ data.orderId || 'N/A' }}</span>
                                        </template>
                                    </Column>
                                    <template #empty>
                                        <div class="text-center py-4">
                                            <i class="pi pi-inbox" style="font-size: 1.5rem; color: var(--surface-400)"></i>
                                            <p class="mt-2 text-color-secondary">No payments found</p>
                                        </div>
                                    </template>
                                </DataTable>
                                <p v-else class="Label">No payments for this student.</p>
                            </div>

                            <Divider />

                            <div>
                                <h3 class="Paragraph-Medium font-semibold mb-3">Class Session Payments ({{ classSessionPayments.length }})</h3>
                                <DataTable v-if="classSessionPayments.length" :value="classSessionPayments" class="p-datatable-sm" dataKey="_id"
                                    paginator :rows="10" :rowsPerPageOptions="[5, 10, 20]">
                                    <Column header="Class Fee">
                                        <template #body="{ data }">{{ formatCurrency(data.classFee, data.currency) }}</template>
                                    </Column>
                                    <Column :header="`Commission (${classSessionPayments[0]?.commissionRate || 0}%)`">
                                        <template #body="{ data }">{{ formatCurrency(data.platformCommission, data.currency) }}</template>
                                    </Column>
                                    <Column header="Tutor Payout">
                                        <template #body="{ data }">{{ formatCurrency(data.tutorPayout, data.currency) }}</template>
                                    </Column>
                                    <Column header="Charge Type">
                                        <template #body="{ data }">{{ cleanupLables(data.chargeType) || 'N/A' }}</template>
                                    </Column>
                                    <Column header="Status">
                                        <template #body="{ data }">
                                            <Tag :value="cleanupLables(data.status)" :severity="paymentStatusSeverity(data.status)" rounded />
                                        </template>
                                    </Column>
                                    <Column header="Date">
                                        <template #body="{ data }">{{ formatToDisplayDate(data.created_at) }}</template>
                                    </Column>
                                    <template #empty>
                                        <div class="text-center py-4">
                                            <i class="pi pi-inbox" style="font-size: 1.5rem; color: var(--surface-400)"></i>
                                            <p class="mt-2 text-color-secondary">No class session payments found</p>
                                        </div>
                                    </template>
                                </DataTable>
                                <p v-else class="Label">No class session payments for this student.</p>
                            </div>
                        </TabPanel>

                        <!-- ===================== ACTIVITY TAB ===================== -->
                        <TabPanel value="activity">
                            <h3 class="Paragraph-Medium font-semibold mb-3">Reviews Given ({{ reviews.length }})</h3>
                            <DataTable v-if="reviews.length" :value="reviews" class="p-datatable-sm" dataKey="_id"
                                paginator :rows="10" :rowsPerPageOptions="[5, 10, 20]">
                                <Column header="Tutor">
                                    <template #body="{ data }">
                                        <div class="flex flex-column">
                                            <span>{{ data.tutorId?.firstName }} {{ data.tutorId?.lastName }}</span>
                                            <small class="text-gray-500">{{ data.tutorId?.email }}</small>
                                        </div>
                                    </template>
                                </Column>
                                <Column header="Rating">
                                    <template #body="{ data }">
                                        <Rating :modelValue="data.rating" readonly />
                                    </template>
                                </Column>
                                <Column header="Review">
                                    <template #body="{ data }">{{ data.review || 'N/A' }}</template>
                                </Column>
                                <Column header="Published">
                                    <template #body="{ data }">
                                        <Tag :value="data.published ? 'Published' : 'Unpublished'"
                                            :severity="data.published ? 'success' : 'warn'" rounded />
                                    </template>
                                </Column>
                                <Column header="Date">
                                    <template #body="{ data }">{{ formatToDisplayDate(data.created_at) }}</template>
                                </Column>
                                <template #empty>
                                    <div class="text-center py-4">
                                        <i class="pi pi-inbox" style="font-size: 1.5rem; color: var(--surface-400)"></i>
                                        <p class="mt-2 text-color-secondary">No reviews found</p>
                                    </div>
                                </template>
                            </DataTable>
                            <p v-else class="Label">No reviews given by this student.</p>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps } from "vue";
import { getStudentDetails } from "@/api/admin";
import { cleanupLables, formatToDisplayDate, formatToDisplayTime } from "@/utils/cleanups";
import { generateSignedUrl } from "@/api/storage";
import blankProfile from '@/assets/blank-profile.png';

import Button from 'primevue/button';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Divider from 'primevue/divider';
import Rating from 'primevue/rating';

const props = defineProps({
    studentId: {
        type: String,
        required: true,
    },
});

const details = ref(null);
const isLoading = ref(false);
const loadError = ref(false);
const profilePhoto = ref(blankProfile);

const profile = computed(() => details.value?.profile || {});
const notes = computed(() => details.value?.academic?.notes || []);
const examResults = computed(() => details.value?.academic?.examResults || []);
const classes = computed(() => details.value?.classes?.classes || []);
const instances = computed(() => details.value?.classes?.instances || []);
const reschedules = computed(() => details.value?.classes?.reschedules || []);
const subscriptions = computed(() => details.value?.billingAndPayments?.subscriptions || []);
const payments = computed(() => details.value?.billingAndPayments?.payments || []);
const classSessionPayments = computed(() => details.value?.billingAndPayments?.classSessionPayments || []);
const reviews = computed(() => details.value?.activity?.reviewsGiven || []);

const totalPaid = computed(() => {
    return payments.value
        .filter(p => p.status === 'Success' || p.status === 'paid')
        .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
});

const formatCurrency = (amount, currency = 'LKR') => {
    if (amount == null || isNaN(amount)) return 'N/A';
    const value = Number(amount).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${currency} ${value}`;
};

const statusSeverity = (status) => {
    const s = String(status || '').toLowerCase();
    if (['active', 'confirmed', 'completed', 'paid', 'success', 'verified'].includes(s)) return 'success';
    if (['pending', 'upcoming', 'unpaid', 'hold'].includes(s)) return 'warn';
    if (['inactive', 'rejected', 'failed', 'cancelled', 'canceled', 'disabled'].includes(s)) return 'danger';
    if (['refunded'].includes(s)) return 'info';
    return 'secondary';
};

const paymentStatusSeverity = (status) => {
    const s = String(status || '').toLowerCase();
    if (['success', 'paid'].includes(s)) return 'success';
    if (['failed'].includes(s)) return 'danger';
    if (['refunded'].includes(s)) return 'info';
    if (['pending', 'unpaid', 'hold'].includes(s)) return 'warn';
    return 'secondary';
};

const fetchProfilePhoto = async () => {
    const url = profile.value?.profilePhotoURL;
    if (!url) {
        profilePhoto.value = blankProfile;
        return;
    }
    try {
        profilePhoto.value = await generateSignedUrl(url);
    } catch (err) {
        profilePhoto.value = blankProfile;
    }
};

const fetchDetails = async () => {
    isLoading.value = true;
    loadError.value = false;
    try {
        const response = await getStudentDetails(props.studentId);
        details.value = response.data.data;
        await fetchProfilePhoto();
    } catch (error) {
        console.error("Failed to fetch student details:", error);
        loadError.value = true;
    } finally {
        isLoading.value = false;
    }
};

onMounted(fetchDetails);
</script>

<style scoped>
.student-details-wrapper {
    width: 100%;
}

.profile-header-card {
    padding: 1.5rem;
}

.profile-image {
    width: 96px;
    height: 96px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid var(--accent-100);
}

.stat-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: var(--accent-50);
    border-radius: 8px;
    padding: 0.75rem 1.25rem;
    min-width: 90px;
}

.stat-value {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--accent-700);
    font-family: 'Raleway', sans-serif;
}

.stat-label {
    font-size: 0.7rem;
    color: var(--gray-600);
    font-family: 'Poppins', sans-serif;
}

.guardian-box {
    border: 1px solid var(--gray-300);
    border-radius: 8px;
    padding: 1rem;
    background: var(--gray-200);
}

::v-deep(.p-datatable .p-datatable-thead > tr > th) {
    background: var(--gray-200);
    color: var(--gray-600);
    font-family: 'Poppins', sans-serif;
    font-size: 0.85rem;
    font-weight: 500;
}

::v-deep(.p-datatable .p-datatable-tbody > tr > td) {
    font-family: 'Poppins', sans-serif;
    font-size: 0.85rem;
    color: var(--gray-600);
}
</style>
